import {
  Button,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
  Stack,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
} from '@carbon/react';
import { View } from '@carbon/icons-react';
import {
  asProblem,
  BackLink,
  hasPermission,
  ListTable,
  PageHeader,
  problemMessage,
  StatusTag,
  useAdmin,
  useFormat,
  useReferenceOptions,
} from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useParams } from 'react-router';
import { ErrorNotice } from '../common/Notices';
import {
  getOrder,
  orderKey,
  ORDERS_REVEAL,
  revealContact,
  STATUS_TAG,
  type OnlineOrderDetail,
  type OnlineOrderLine,
  type OrderStatus,
  type PaymentMethod,
} from './onlineOrdersApi';
import './onlineOrders.scss';

/**
 * One website or app order (FR-ONL-05, FR-ONL-15): where it is (each step's time, as a Carbon progress indicator), what was ordered
 * and priced, the payment, and the customer with the phone masked and the address left out; *Reveal contact* shows both and is
 * audited. Read only.
 */
export function OnlineOrderPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const order = useQuery({ queryKey: orderKey(id), queryFn: () => getOrder(id), refetchInterval: 15_000 });
  if (order.error)
    return <ErrorNotice title={problemMessage(t, asProblem(order.error))} onRetry={() => void order.refetch()} />;
  if (!order.data) return null;
  return <Detail order={order.data} />;
}

function Detail({ order: o }: { order: OnlineOrderDetail }) {
  const { t } = useTranslation();
  const { dateTime, money } = useFormat();
  const { session } = useAdmin();
  const stores = useReferenceOptions('org.store');
  const channels = useReferenceOptions('config.sales_channel');
  const reveal = useMutation({ mutationFn: () => revealContact(o.id) });
  const status = o.status as OrderStatus;
  const amount = (value: number | string) => money(Number(value), o.currencyCode);
  const ended = status === 'rejected' || status === 'cancelled';

  const steps: { label: I18nKey; at: string | null | undefined }[] = [
    { label: 'onlineOrders.steps.received', at: o.receivedAt },
    { label: 'onlineOrders.steps.relayed', at: o.relayedAt },
    { label: 'onlineOrders.steps.accepted', at: o.acceptedAt },
    { label: 'onlineOrders.steps.ready', at: o.readyAt },
    ...(o.fulfilment === 'delivery'
      ? [{ label: 'onlineOrders.steps.outForDelivery' as const, at: o.outForDeliveryAt }]
      : []),
    { label: o.fulfilment === 'dine_in' ? 'onlineOrders.steps.served' : 'onlineOrders.steps.completed', at: o.completedAt },
  ];
  const reached = steps.reduce((last, step, i) => (step.at ? i : last), 0);

  const rows: [I18nKey, ReactNode][] = [
    [
      'onlineOrders.columns.status',
      <StatusTag key="s" status={STATUS_TAG[status] ?? 'pending'}>
        {t(`onlineOrders.status.${status}`, { defaultValue: o.status })}
      </StatusTag>,
    ],
    ['onlineOrders.columns.store', stores.data?.find((s) => s.value === o.storeId)?.label ?? '—'],
    ['onlineOrders.columns.channel', channels.data?.find((c) => c.value === o.salesChannelId)?.label ?? '—'],
    [
      'onlineOrders.columns.fulfilment',
      o.fulfilment ? t(`onlineOrders.fulfilment.${o.fulfilment as 'delivery' | 'pickup' | 'dine_in'}`) : '—',
    ],
    [
      'onlineOrders.columns.payment',
      o.paymentMethod
        ? `${t(`onlineOrders.payment.${o.paymentMethod as PaymentMethod}`)}${o.isPrepaid ? ` · ${t('onlineOrders.prepaid')}` : ''}`
        : '—',
    ],
    ['onlineOrders.columns.customer', o.customerName ?? '—'],
    ['onlineOrders.fields.phone', o.maskedPhone ?? '—'],
    ['onlineOrders.fields.note', o.note ?? '—'],
  ];
  if (o.prepMinutes)
    rows.push(['onlineOrders.fields.prepMinutes', t('onlineOrders.minutes', { count: o.prepMinutes })]);
  if (o.acceptDeadlineAt && (status === 'relayed' || status === 'received'))
    rows.push(['onlineOrders.fields.acceptBy', dateTime(o.acceptDeadlineAt)]);

  const totals: [I18nKey, number | string][] = [
    ['onlineOrders.totals.subtotal', o.subtotal],
    ...(Number(o.discountTotal)
      ? [['onlineOrders.totals.discounts', -Number(o.discountTotal)] as [I18nKey, number]]
      : []),
    ...(Number(o.chargeTotal) ? [['onlineOrders.totals.charges', o.chargeTotal] as [I18nKey, number | string]] : []),
    ...(o.fulfilment === 'delivery'
      ? [['onlineOrders.totals.deliveryFee', o.deliveryFee] as [I18nKey, number | string]]
      : []),
    ['onlineOrders.totals.tax', o.taxTotal],
    ['onlineOrders.totals.total', o.total],
  ];

  return (
    <section className="pos-admin-page">
      <BackLink to="/online/orders">{t('onlineOrders.title')}</BackLink>
      <PageHeader
        title={o.orderNumber ?? t('onlineOrders.title')}
        description={t('onlineOrders.receivedAt', { when: dateTime(o.receivedAt) })}
        actions={
          hasPermission(session, ORDERS_REVEAL) && (o.maskedPhone || o.hasAddress) ? (
            <Button kind="tertiary" renderIcon={View} disabled={reveal.isPending} onClick={() => reveal.mutate()}>
              {t('onlineOrders.reveal')}
            </Button>
          ) : undefined
        }
      />
      <Stack gap={6}>
        {reveal.data && (
          <InlineNotification
            kind="warning"
            lowContrast
            title={t('onlineOrders.revealed')}
            subtitle={[reveal.data.phone, [reveal.data.address, reveal.data.addressDetail].filter(Boolean).join(', ')]
              .filter(Boolean)
              .join(' · ')}
            onClose={() => reveal.reset()}
          />
        )}
        {reveal.error && <ErrorNotice title={problemMessage(t, asProblem(reveal.error))} />}
        {ended && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={t(`onlineOrders.status.${status}`)}
            subtitle={
              o.rejectReason
                ? t(`onlineOrders.reasons.${o.rejectReason}` as I18nKey, { defaultValue: o.rejectReason })
                : undefined
            }
          />
        )}
        {!ended && (
          <ProgressIndicator currentIndex={reached} spaceEqually aria-label={t('onlineOrders.progress')}>
            {steps.map((step, i) => (
              <ProgressStep
                key={step.label}
                label={t(step.label)}
                secondaryLabel={step.at ? dateTime(step.at) : undefined}
                complete={Boolean(step.at) && i < reached}
                current={i === reached}
              />
            ))}
          </ProgressIndicator>
        )}
        <StructuredListWrapper isCondensed aria-label={t('onlineOrders.summary')}>
          <StructuredListBody>
            {rows.map(([key, value]) => (
              <StructuredListRow key={key}>
                <StructuredListCell noWrap>{t(key)}</StructuredListCell>
                <StructuredListCell>{value}</StructuredListCell>
              </StructuredListRow>
            ))}
          </StructuredListBody>
        </StructuredListWrapper>
        <ListTable<OnlineOrderLine & { id: string }>
          ariaLabel={t('onlineOrders.items')}
          rows={o.lines.map((l, i) => ({ ...l, id: String(i) }))}
          columns={[
            {
              key: 'name',
              header: t('onlineOrders.columns.item'),
              render: (l) => (
                <span className="pos-online-orders__cell">
                  <span>{l.name}</span>
                  {[...l.options, ...l.items.map((x) => `${Number(x.quantity)} × ${x.name}`)].length > 0 && (
                    <span className="pos-online-orders__secondary">
                      {[...l.options, ...l.items.map((x) => `${Number(x.quantity)} × ${x.name}`)].join(', ')}
                    </span>
                  )}
                </span>
              ),
            },
            { key: 'quantity', header: t('onlineOrders.columns.quantity'), render: (l) => Number(l.quantity) },
            { key: 'lineTotal', header: t('onlineOrders.columns.amount'), render: (l) => amount(l.lineTotal) },
          ]}
        />
        <StructuredListWrapper
          isCondensed
          aria-label={t('onlineOrders.totals.title')}
          className="pos-online-orders__totals"
        >
          <StructuredListBody>
            {totals.map(([key, value]) => (
              <StructuredListRow key={key}>
                <StructuredListCell noWrap>{t(key)}</StructuredListCell>
                <StructuredListCell className="pos-online-orders__amount">{amount(value)}</StructuredListCell>
              </StructuredListRow>
            ))}
          </StructuredListBody>
        </StructuredListWrapper>
      </Stack>
    </section>
  );
}
